import { WizNumberUtils } from './wiz-number.utils';
import { WizDateLocaleObj } from './../obj/date/wiz-date-locale.obj';
import { DateTime } from 'luxon';
import { WizStringUtils } from './wiz-string.utils';
import { WizCoreTranslationManager as WizTranslationManager } from '../services/wiz-translation.manager';
/** classe utilitaire de gestion des temps (dates / heures) */
export class WizDateUtils {
  
  // pour injection depuis la lib component
  public static tradInstance: WizTranslationManager = null;

  /** Format par défaut pour les heures (string) */
  public static readonly TIME_MASK: string = 'HHmm';

  /** Format par défaut pour les jours (number) */
  public static readonly DAY_MASK: string = 'yyyyMMdd';

  /** format number sur 14 */
  public static readonly FULL_MASK: string = 'yyyyMMddHHmmss';

  /** format number sur 17 (ms) */
  public static readonly MILLISEC_MASK: string = 'yyyyMMddHHmmssSSS';


  /** Format de date Intl numérique (année sur 4 digits, mois et jour sur 2 digits) */
  private static DATE_FORMAT_NUMERIC: Intl.DateTimeFormatOptions = {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  };

  /** Caractères de séparation acceptés */
  public static readonly ACCEPTED_SEPARATORS: string = '.-/|;:\\,';




  /** Date du jour */
  public static get TODAY(): Date {
    return new Date();
  }

  /** Année en cours */
  public static get CURRENT_YEAR(): number {
    return this.TODAY.getFullYear();
  }

  /** Mois en cours */
  public static get CURRENT_MONTH(): number {
    return this.TODAY.getMonth();
  }

  /** jour en cours */
  public static get CURRENT_DAY(): number {
    return this.TODAY.getDate();
  }

  /** heure en cours */
  public static get CURRENT_HOUR(): number {
    return this.TODAY.getHours();
  }

  /** minute en cours */
  public static get CURRENT_MINUTE(): number {
    return this.TODAY.getMinutes();
  }

  /** Modes pour les datefields (vue de sélection la plus précise autorisée) */
  public static readonly DATE_MODE: string = 'month';
  public static readonly MONTH_MODE: string = 'year';
  public static readonly YEAR_MODE: string = 'multi-year';

  /** Date du jour sans les heures/minutes/secondes */
  public static get TODAY_WITHOUT_HOURS(): Date {
    return this.clearTime(new Date());
  }

  /** Format par défaut pour les jours (string) */
  public static readonly DAY_FORMAT_MASK: string = 'MM/dd/y';
  /** séparateur par défaut pour les jours (string) */
  public static readonly DAY_FORMAT_SEPARATOR: string = '/';

  /** Format par défaut pour les heures (string) */
  public static readonly HOUR_FORMAT_MASK: string = 'HH:mm';
  /** séparateur par défaut pour les heures (string) */
  public static readonly HOUR_FORMAT_SEPARATOR: string = ':';

  /** Format par défaut pour les jours (string) */
  public static readonly FULL_FORMAT_MASK: string = 'MM/dd/y HH:mm';

  /**
   * retourne maintenant en number au format yyyyMMddHHmmssSSS
   * @param format format de la convertion
   */
  public static NOW(): number {
    return this.toMillisec(this.TODAY);
  }

  /**
   * retourne la date en number au format yyyyMMddHHmmssSSS
   * @param date 
   * @returns 
   */
  public static toMillisec(date: Date): number {
    return Number(DateTime.fromJSDate(date).toFormat(this.MILLISEC_MASK));
  }

  /**
   * construction de la date sur la base du string.
   * formats incomplets autorisés pour saisie rapide dans les dateFields
   */
  public static rawStringToDate(
    str: string,
    mask: string = this.DAY_FORMAT_MASK,
    separator: string = this.DAY_FORMAT_SEPARATOR
  ): Date {
    let result: Date = null;

    // moins de 3 digits, on peut rien faire
    if (str.length < 3) return null;

    // le masque me donne les 3 zones
    let dayValue: string = '';
    let monthValue: string = '';
    let yearValue: string = '';

    // je n'ai pas de séparateur, j'en met
    if (str.indexOf(separator) < 0) {
      // les positions des séparateurs
      const separatorPositions: number[] = [];
      let temp: string;
      let i: number = 0;
      // on répère les positions sur le masque
      for (i = 0; i < mask.length; i++) {
        temp = '' + mask.charAt(i);
        if (mask.charAt(i) === separator) {
          separatorPositions.push(i);
        }
      }

      // on les applique sur la chaine
      separatorPositions.forEach((posx) => {
        if (str.length >= posx) {
          str = str.slice(0, posx) + separator + str.slice(posx, str.length);
        }
      });
      // console.log('rawStringToDate',separatorPositions, str,mask);
    }

    // j'ai des séparateurs, je plit en 2 ou 3
    const part: string[] = str.split(separator);
    const partKind: string[] = mask.split(separator);

    // console.log('rawStringToDate2',part,partKind);

    let temp: string;
    let i: number = 0;
    // boucle sur les morceaux pour les ventiler correctement
    for (i = 0; i < 3; i++) {
      // console.log(partKind[i], part[i]);
      switch (partKind[i]) {
        case 'DD': // dayPart
          dayValue = part[i];
          break;
        case 'MM': // monthPart
          monthValue = part[i];
          break;
        case 'YYYY': // yearPart
          yearValue = part[i];
          break;
      }
    }
    // console.log('rawStringToDate3',dayValue, monthValue, yearValue);

    // on complète si nécessaire
    if (!yearValue) {
      // pas d'année, l'année en cours
      yearValue = this.CURRENT_YEAR.toString();
    }
    if (yearValue.length == 2) {
      // complétion 4 digits sur l'année
      yearValue = '20' + yearValue;
    }
    if (!monthValue) {
      // pas de mois, mois en cours
      monthValue = this.CURRENT_MONTH.toString();
    }
    if (!dayValue) {
      dayValue = this.CURRENT_DAY.toString();
    }

    // on vérifie d'abord que c'est valide
    const checkStr: string = yearValue + '-' + monthValue + '-' + dayValue;
    let d = DateTime.fromFormat(checkStr, 'y-M-d');
    result = d;
    // console.log('rawStringToDate4',checkStr,d.isValid,d.invalidReason);
    return result;
  }

  /**
   * construction d'une heure HHmm à partir d'une chaîne de caractères
   * formats incomplets autorisés pour saisie rapide
   */
  public static rawStringToHour(
    str: string,
    mask: string = this.HOUR_FORMAT_MASK,
    separator: string = this.HOUR_FORMAT_SEPARATOR
  ): string {
    let result: string = null;
    let hourStr: string = str;
    let meridiemStr: string;

    if(!str) {
      return null;
    }

    if (str.indexOf(' ') > 0) {
      const strArr: string[] = str.split(' ');
      hourStr = strArr[0];
      if (strArr.length > 1) {
        meridiemStr = strArr[1];
      }
    }
    // moins de 2 digits, on peut rien faire
    else if (hourStr.length < 2) return null;

    // le masque me donne les 2 zones
    let hourValue: string = '';
    let minuteValue: string = '';

    // je n'ai pas de séparateur, j'en met
    if (hourStr.indexOf(separator) < 0) {
      if (hourStr.length == 3) {
        // 3 caractères, on considère de préférence h:mm
        hourStr = '0' + hourStr.slice(0, 1) + separator + hourStr.slice(1, hourStr.length);
      } else {
        // les positions des séparateurs
        const separatorPositions: number[] = [];
        let temp: string;
        let i: number = 0;
        // on répère les positions sur le masque
        for (i = 0; i < mask.length; i++) {
          temp = '' + mask.charAt(i);
          if (mask.charAt(i) === separator) {
            separatorPositions.push(i);
          }
        }

        // on les applique sur la chaine
        separatorPositions.forEach((posx) => {
          if (hourStr.length >= posx) {
            hourStr = hourStr.slice(0, posx) + separator + hourStr.slice(posx, hourStr.length);
          }
        });
      }
    }

    // j'ai des séparateurs, je split en 2
    const part: string[] = hourStr.split(separator);
    const partKind: string[] = mask.split(separator);

    // console.log('rawStringToDate2',part,partKind);

    let i: number = 0;
    // boucle sur les morceaux pour les ventiler correctement
    for (i = 0; i < 3; i++) {
      // console.log(partKind[i], part[i]);
      switch (partKind[i]) {
        case 'HH': // hour part
        case 'hh': // hour part with meridiem
          hourValue = part[i];
          break;
        case 'mm': // minute part
          minuteValue = part[i];
          break;
      }
    }

    // on complète si nécessaire
    if (!hourValue) {
      // pas d'heure, l'heure en cours
      hourValue = this.CURRENT_HOUR.toString();
    } else if (meridiemStr) {
      const lowerMeridiemStr: string = meridiemStr.toLowerCase();
      if (lowerMeridiemStr === 'p' || lowerMeridiemStr === 'pm') {
        const hourNumber: number = WizNumberUtils.parseNumber(hourValue);
        if (hourNumber < 12) {
          // en mode pm, 12 c'est 12 sinon on ajoute 12
          hourValue = (hourNumber + 12).toString();
        }
      }
    }
    if (!minuteValue) {
      // pas de minute, minute en cours
      minuteValue = this.CURRENT_MINUTE.toString();
    }

    // on vérifie d'abord que c'est valide
    const checkStr: string = hourValue + '-' + minuteValue;
    let d = DateTime.fromFormat(checkStr, 'HH-mm');
    result = d && !d.invalid ? hourValue + minuteValue : undefined;
    // console.log('rawStringToDate4',checkStr,d.isValid,d.invalidReason);
    return result;
  }

  /** Locale actuellement utilisée pour les dates */
  public static getLocale(): string {
    return this.tradInstance.dateLocale;
  }

  /** Retourne le décalage horaire du device par rapport à l'UTC, en heures */
  public static gmt(): number {
    return (new Date().getTimezoneOffset() / 60) * -1;
  }

  // #region Day / Month Names

  /**
   * Retourne la liste des jours de la semaine, dans la langue configurée
   * @param short Si short = true, retourne le nom dans un format court
   */
  public static getDayNames(short: boolean = false): string[] {
    const baseDate = new Date(Date.UTC(2017, 0, 2)); // just a Monday
    const weekDays = [];
    const format: any = short ? 'short' : 'long';
    for (let i = 0; i < 7; i++) {
      weekDays.push(baseDate.toLocaleDateString(this.getLocale(), { weekday: format }));
      baseDate.setDate(baseDate.getDate() + 1);
    }
    return weekDays;
  }

  /** Retourne le nom d'un jour de la semaine, dans la langue configurée
   * @param dayIndex
   *          L'index du jour, démarre à 0
   * @param short
   *          Si short = true, retourne le nom dans un format court
   */
  public static getDayName(dayIndex: number, short: boolean = false): string {
    const baseDate = new Date(Date.UTC(2017, 0, 2));
    baseDate.setDate(baseDate.getDate() + dayIndex);
    const format: any = short ? 'short' : 'long';
    return baseDate.toLocaleDateString(this.getLocale(), { weekday: format });
  }

  /** Retourne la liste des mois de l'année, dans la langue configurée
   * @param short
   *          Si short = true, retourne le nom dans un format court
   */
  public static getMonthNames(short: boolean = false): string[] {
    const baseDate = new Date(Date.UTC(2017, 0, 1)); // just a Monday
    const months = [];
    const format: any = short ? 'short' : 'long';
    for (let i = 0; i < 11; i++) {
      months.push(baseDate.toLocaleDateString(this.getLocale(), { month: format }));
      baseDate.setMonth(baseDate.getMonth() + 1);
    }
    return months;
  }

  /** Retourne le nom d'un mois de l'année, dans la langue configurée
   * @param monthIndex
   *          L'index du mois, démarre à 0
   * @param short
   *          Si short = true, retourne le nom dans un format court
   */
  public static getMonthName(monthIndex: number, short: boolean = false): string {
    const baseDate = new Date(Date.UTC(2017, 0, 1));
    baseDate.setMonth(baseDate.getMonth() + monthIndex);
    const format: any = short ? 'short' : 'long';
    return baseDate.toLocaleDateString(this.getLocale(), { month: format });
  }

  // #endregion

  // #region Convert from Date object

  /**
   * Formatage d'une date en string type Mois (Décembre 2015)
   * @param pDate La date à convertir
   */
  public static dateToMonth(pDate: Date): string {
    return pDate ? pDate.toLocaleString(this.getLocale(), { month: 'long', year: 'numeric' }) : '';
  }

  /**
   * Formatage d'une date en string
   * @param pDate La date à convertir
   * @param format format de la convertion
   */
  public static dateToString(pDate: Date | string, pMask: string = this.DAY_MASK): string {
    return pDate && pMask ? DateTime.fromJSDate(pDate).toFormat(pMask) : undefined;
  }

  /**
   * Conversion d'une date formattée selon pMask (string) en Date
   * @param pStrDate le string
   * @param pMask le masque de l'heure
   */
  public static stringToDate(pStrDate: string, pMask: string = this.DAY_MASK): Date {
    if (WizStringUtils.isEmpty(pStrDate)) {
      return null;
    }
    pStrDate = String(pStrDate); // on cast de force pour prb sur number.charAt
    let mask: string;
    let temp: string;
    let dateString: string = '';
    let monthString: string = '';
    let yearString: string = '';
    let j: number = 0;
    let i: number = 0;

    const n: number = pMask.length;

    for (i = 0; i < n; i++, j++) {
      temp = '' + String(pStrDate).charAt(j);
      mask = '' + pMask.charAt(i);

      if (mask === 'M') {
        if (isNaN(Number(temp))) {
          j--;
        } else {
          monthString += temp;
        }
      } else if (mask === 'd') {
        if (isNaN(Number(temp))) {
          j--;
        } else {
          dateString += temp;
        }
      } else if (mask === 'y') {
        yearString += temp;
      } else if (this.ACCEPTED_SEPARATORS.indexOf(mask) > 0) {
        // séparateur
        continue;
      } else if (isNaN(Number(temp)) && temp !== ' ') {
        return null;
      }
    }
    if (!WizStringUtils.isEmpty(yearString)) {
      yearString = yearString.trim();
    }
    if (!WizStringUtils.isEmpty(monthString)) {
      monthString = monthString.trim();
    }
    if (!WizStringUtils.isEmpty(dateString)) {
      dateString = dateString.trim();
    }

    // temp = '' + pStrDate.charAt(pMask.length - i + j);

    // if (!(temp === '') && temp !== ' ') {
    //   return null;
    // }

    const monthNum: number = Number(monthString);
    const dayNum: number = Number(dateString);
    const yearNum: number = Number(yearString);

    if (isNaN(yearNum) || isNaN(monthNum) || isNaN(dayNum) || monthNum === 0 || dayNum === 0) {
      return null;
    }

    const newDate: Date = new Date(yearNum, monthNum - 1, dayNum);

    if (dayNum !== newDate.getDate() || monthNum - 1 !== newDate.getMonth()) {
      return null;
    }
    return newDate;
  }

  /** Formatage d'une date en string
   * @param pDate La date à convertir
   * @param format format de la convertion
   */
  public static dateToLocalizedString(
    pDate: Date | string,
    options: Intl.DateTimeFormatOptions = this.DATE_FORMAT_NUMERIC
  ): string {
    return pDate ? pDate.toLocaleString(this.getLocale(), options) : '';
  }

  /** Conversion d'une date en nombre au format yyyyMMdd
   * @param pDate La date à convertir
   * @param pMask Le format à utiliser, yyyyMMdd par défaut
   */
  public static dateToNumber(pDate: Date | string, pMask: string = this.DAY_MASK): number {
    return pDate ? parseInt(this.dateToString(pDate, pMask), 10) : undefined;
  }

  /**
   * Conversion d'un number au format YYYYMMDD en date
   * @param value La date à convertir en tant que nombre au format YYYYMMDD
   * @return La date en tant qu'objet Date
   */
  public static numberToDate(value: number): Date {
    if (!value || isNaN(value)) {
      // pas de null Pointer
      return null;
    }
    try {

      let fullZero: string = "00000000000000";
      // let d14: string = (fullZero + value.toString()).slice(-14);
      let d14: string = (value.toString() + fullZero).slice(0, 14);
      const datePattern = /^(\d{4})(\d{2})(\d{2})(\d{2})(\d{2})(\d{2})$/;
      const [,month, day, year, hour, min, sec] = datePattern.exec(d14);
      // console.log('const', month, day, year, hour, min, sec);
      let res: Date = new Date(`${month}, ${day} ${year}`);
      res.setHours(Number(hour), Number(min), Number(sec))
      // console.log('res', res);
      return res;

      // const datePattern = /^(\d{4})(\d{2})(\d{2})$/;
      // const [, month, day, year] = datePattern.exec(value.toString());
      // return new Date(`${month}, ${day} ${year}`);
    } catch (e: any) {
      return null;
    }
  }

  /**
   * Conversion d'une heure au format HHmm en date
   * @param value L'heure à convertir
   * @return La date en tant qu'objet Date
   */
  public static hourToDate(value: string): Date {
    if (!value || value.length !== 4) {
      return null;
    }
    const date: Date = new Date();
    date.setHours(WizNumberUtils.parseNumber(value.substr(0, 2)));
    date.setMinutes(WizNumberUtils.parseNumber(value.substr(2, 2)));
    return date;
  }

  /**
   * Formatage d'un number en string date
   * @param pDate  pDate La date à convertir
   * @param format  format de la convertion
   */
  public static numberToStringDate(pDate: number, format: string): string {
    return pDate ? this.dateToString(this.numberToDate(pDate), format) : undefined;
  }

  /**
   * Formatage d'un number en string date selon la locale
   * @param pDate  pDate La date à convertir
   * @param format  format de la convertion
   */
  public static numberToLocalizedString(
    pDate: number,
    options: Intl.DateTimeFormatOptions = this.DATE_FORMAT_NUMERIC
  ): string {
    let result: string = '';
    try {
      result = !!pDate && Number(pDate) > 0 ? this.dateToLocalizedString(this.numberToDate(pDate), options) : undefined;
    } catch (e: any) {
      console.log('bad date', e);
    }
    return result;
  }

  /** Conversion d'une date en string au format HHmm
   * @param pDate La date à convertir
   */
  public static dateToTimeString(pDate: Date, pMask: string = this.TIME_MASK): string {
    return this.dateToString(pDate, pMask);
  }


  /** 
   * conversion d'un number sur 14 / 8 en date
   * @param pStrDate La date à convertir, sous forme de string
   * @param pMask le masque de l'heure
   */
  public static stringToTime(pStrDate: string): Date {
    if (WizStringUtils.isEmpty(pStrDate)) {
      return null;
    }

    if(pStrDate.length === 8) {
      pStrDate = pStrDate + '000000'; // voila, on a 14
    }

    let yearString = pStrDate.slice(0,4);
    let monthString = pStrDate.slice(4,6);
    let dateString = pStrDate.slice(6,8);
    let hourString = pStrDate.slice(8,10);
    let minuteString = pStrDate.slice(10,12);
    let secondString = pStrDate.slice(12,14);
    
    const secondNum: number = Number(secondString);
    const minuteNum: number = Number(minuteString);
    const hourNum: number = Number(hourString);
    const dayNum: number = Number(dateString);
    const monthNum: number = Number(monthString);
    const yearNum: number = Number(yearString);
    let newDate: Date = new Date(yearNum, monthNum - 1, dayNum, hourNum, minuteNum, secondNum);
    return newDate;
  }

  /** Vérifie si les paramètres forment une date valide
   * @param year Une année (number)
   * @param month Un mois (number)
   * @param date Le jour du mois (number)
   *
   * @return true s'il s'agit d'une date valide
   *         false sinon
   */
  public static isDate(year: number, month: number, date: number): boolean {
    const myDate: Date = new Date(year, month, date);
    return myDate.getFullYear() === year && myDate.getMonth() === month && myDate.getDate() === date;
  }

  /** détermine si 2 dates sont égales, gestion du null
   * @param pFirst la première date à comparer
   * @param PSecond Les seconde date à comparer
   * @param pIgnoreTime Pas de prise en compte des heures
   */
  public static sameDates(pFirst: Date, PSecond: Date, pIgnoreTime: boolean = true): boolean {
    if (pFirst && !PSecond) {
      return false;
    }
    if (PSecond && !pFirst) {
      return false;
    }
    if (!pFirst && !PSecond) {
      return true;
    }
    return this.compareDates(pFirst, PSecond, pIgnoreTime) == 0;
  }

  /** Comparaison de deux dates
   * @param pDateFirst la première date à comparer
   * @param pDateBis Les seconde date à comparer
   * @param pIgnoreTime Pas de prise en compte des heures
   * @return -1 si la première date est ultérieure à la seconde
   *          1 si la première date est antérieure à la seconde
   *          0 si les deux dates sont identiques
   */
  public static compareDates(pDateFirst: Date, pDateBis: Date, pIgnoreTime: boolean = true): number {
    if (this.isAfter(pDateFirst, pDateBis, pIgnoreTime)) {
      return -1;
    } else if (this.isBefore(pDateFirst, pDateBis, pIgnoreTime)) {
      return 1;
    } else {
      return 0;
    }
  }

  /** Indique si une date se trouve entre deux dates spécifiées
   * @param dateToCompare La date à vérifier
   * @param startDate La date de début, servant de borne
   * @param endDate La date de fin, servant de borne
   * @param looseEnds Si true, une date identique à l'une des bornes est tout de même considéré comme étant dans la limite
   * @param pIgnoreTime Pas de prise en compte des heures
   * @return true si la date à comparer se trouve entre les dates de début et de fin
   *         false sinon
   */
  public static isBetween(
    dateToCompare: Date,
    startDate: Date,
    endDate: Date,
    looseEnds: boolean = false,
    pIgnoreTime: boolean = true
  ): boolean {
    const afterStartDate: boolean =
      this.isAfter(dateToCompare, startDate, pIgnoreTime) ||
      (looseEnds && this.isSame(dateToCompare, startDate, pIgnoreTime));
    const beforeEndDate: boolean =
      this.isBefore(dateToCompare, endDate, pIgnoreTime) ||
      (looseEnds && this.isSame(dateToCompare, endDate, pIgnoreTime));

    return afterStartDate && beforeEndDate;
  }

  /** Comparaison de deux dates
   * @param pDateFirst la première date à comparer
   * @param pDateBis Les seconde date à comparer
   * @param pIgnoreTime Pas de prise en compte des heures
   * @return  true si pDateFirst est ultérieure à pDateBis
   *          false sinon
   */
  public static isAfter(pDateFirst: Date, pDateBis: Date, pIgnoreTime: boolean = true): boolean {
    if (pIgnoreTime) {
      pDateFirst = this.clearTime(pDateFirst);
      pDateBis = this.clearTime(pDateBis);
    }
    return !!pDateFirst && pDateFirst > pDateBis;
  }

  /** Comparaison de deux dates
   * @param pDateFirst la première date à comparer
   * @param pDateBis Les seconde date à comparer
   * @param pIgnoreTime Pas de prise en compte des heures
   * @return  true si pDateFirst est antérieure à pDateBis
   *          false sinon
   */
  public static isBefore(pDateFirst: Date, pDateBis: Date, pIgnoreTime: boolean = true): boolean {
    if (pIgnoreTime) {
      pDateFirst = this.clearTime(pDateFirst);
      pDateBis = this.clearTime(pDateBis);
    }
    return !!pDateFirst && pDateFirst < pDateBis;
  }

  /** Comparaison de deux dates
   * @param pDateFirst la première date à comparer
   * @param pDateBis Les seconde date à comparer
   * @param pIgnoreTime Pas de prise en compte des heures
   * @return  true si pDateFirst est ultérieure ou identique à pDateBis
   *          false sinon
   */
  public static isAfterOrSame(pDateFirst: Date, pDateBis: Date, pIgnoreTime: boolean = true): boolean {
    return this.isAfter(pDateFirst, pDateBis, pIgnoreTime) || this.isSame(pDateFirst, pDateBis, pIgnoreTime);
  }

  /** Comparaison de deux dates
   * @param pDateFirst la première date à comparer
   * @param pDateBis Les seconde date à comparer
   * @param pIgnoreTime Pas de prise en compte des heures
   * @return  true si pDateFirst est antérieure ou identique à pDateBis
   *          false sinon
   */
  public static isBeforeOrSame(pDateFirst: Date, pDateBis: Date, pIgnoreTime: boolean = true): boolean {
    return this.isBefore(pDateFirst, pDateBis, pIgnoreTime) || this.isSame(pDateFirst, pDateBis, pIgnoreTime);
  }

  /** Comparaison de deux dates
   * @param pDateFirst la première date à comparer
   * @param pDateBis La seconde date à comparer
   * @param pIgnoreTime Pas de prise en compte des heures
   * @return  true si pDateFirst et pDateBis sont identiques
   *          false sinon
   */
  public static isSame(pDateFirst: Date, pDateBis: Date, pIgnoreTime: boolean = true): boolean {
    if (pDateFirst && pDateBis) {
      if (pIgnoreTime) {
        pDateFirst = this.clearTime(pDateFirst);
        pDateBis = this.clearTime(pDateBis);
      }
      return pDateFirst.getTime() === pDateBis.getTime();
    }
    return false;
  }

  /** Comparaison de deux horaires
   * @param pDateFirst le premier horaire à comparer
   * @param pDateBis
   *       Le second horaire à comparer
   * @return  true si pHourFirst est ultérieure à pHourBis
   *          false sinon
   */
  public static isHourAfter(pHourFirst: string, pHourBis: string): boolean {
    return pHourFirst > pHourBis;
  }

  /** Comparaison de deux horaires
   * @param pDateFirst le premier horaire à comparer
   * @param pDateBis
   *       Le second horaire à comparer
   * @return  true si pHourFirst est ultérieure à pHourBis
   *          false sinon
   */
  public static isHourBefore(pHourFirst: string, pHourBis: string): boolean {
    return pHourFirst < pHourBis;
  }

  /** Permet de savoir si une année est bissextile
   * @param pYear L'année
   * @return true si l'année est bissextile
   *         false sinon
   */
  public static isLeapYear(pYear: number): boolean {
    return new Date(pYear, 1, 29).getDate() === 29;
  }

  /** Retourne tous les jours de la semaine d'une date donnée
   * @param pDate La date depuis laquelle se baser
   * @param pFirstDayOfWeek Le premier jour de la semaine qui sera retourné (1 = lundi)
   * @param pNumberOfWeekDays Le nombre de jours à retourner
   * @return Un tableau de dates contenant la liste des jours
   */
  public static weekDates(pDate: Date, pFirstDayOfWeek: number = 1, pNumberOfWeekDays: number = 5): Date[] {
    const days: Date[] = [];
    // Prend la semaine précédente si date < 1er jour de la semaine
    const offset = pDate.getDay() < pFirstDayOfWeek ? -7 : 0;

    // Premier jour de la semaine
    const realStartDate: Date = new Date(
      pDate.getFullYear(),
      pDate.getMonth(),
      pDate.getDate() - pDate.getDay() + pFirstDayOfWeek + offset
    );

    for (let index: number = 0; index < pNumberOfWeekDays; index++) {
      days.push(this.add(realStartDate, { days: 1 }) as Date);
    }
    return days;
  }

  /** Arrondi les minutes d'une date
   * @param pDate La date à arrondir
   * @param modulo La tranche selon laquelle arrondir
   * @return La date arrondie
   */
  public static roundMinutes(pDate: Date, modulo: number): Date {
    const mnToBeRounded = pDate.getMinutes();
    const seconds = pDate.getSeconds();

    const exceeding = mnToBeRounded % modulo;
    const roundedMinutes =
      exceeding * 60 + seconds < (modulo * 60) / 2
        ? mnToBeRounded - exceeding // Arrondi inférieur
        : mnToBeRounded + modulo - exceeding; // Arrondi supérieur

    pDate.setMinutes(roundedMinutes);
    pDate.setSeconds(0);
    return pDate;
  }

  /** Retourne le number avec un zéro non-significatif si nécessaire (8 -> 08)
   * @param pValue Le nombre auquel ajouter le zéro non-significatif
   * @return Le nombre, sur deux chiffres
   */
  public static numberOnTwoDigits(pValue: number): string {
    // undefined === null -> false     |     undefined == null -> true
    // tslint:disable-next-line: triple-equals
    if (pValue == void 0) {
      return '' + pValue;
    }
    return pValue < 10 ? '0' + pValue.toString() : pValue.toString();
  }

  /**
   * Ajout de jour/month/year
   * @param pDate la date à modifier
   * @param options options de modification
   * @return number | date en fonction du type passé en paramètre
   */
  public static add(pDate: Date | number, options: { days?: number; months?: number; years?: number }): Date | number {
    if (pDate) {
      let date: Date;
      if (typeof pDate === 'number') {
        date = WizDateUtils.numberToDate(pDate);
      } else {
        date = new Date(pDate.getTime());
      }
      if (options && options.years) {
        date.setFullYear(date.getFullYear() + options.years);
      }
      if (options && options.months) {
        const realDate: number = date.getDate();
        // On passe au jour 1 avant d'ajouter les mois
        date.setDate(1);
        date.setMonth(date.getMonth() + options.months);
        // On remet la date, ou le max du mois si inférieur
        const maxMonthDays: number = this.daysInMonth(date.getMonth(), date.getFullYear());
        date.setDate(maxMonthDays < realDate ? maxMonthDays : realDate);
      }
      if (options && options.days) {
        date.setDate(date.getDate() + options.days);
      }
      return typeof pDate === 'number' ? WizDateUtils.dateToNumber(date) : date;
    }
    return undefined;
  }

  /**
   * Retourne le nombre de jours pour un mois donné
   * @param month L'index du mois
   * @param year L'année
   */
  public static daysInMonth(month: number, year: number): number {
    const mydate: Date = new Date(year, month + 1, 0);
    return mydate.getDate();
  }

  /** Liste des jours  entre 2 dates, incluses
   * @param pStartDate La date de début
   * @param pEndDate La date de fin
   * @return Un tableau de dates contenant les jours ouvrés entre pStartDate et pEndDate, les deux dates étant incluses
   */
  public static getDays(startDate: Date | number, endDate: Date | number): Date[] {
    const result: Date[] = [];

    if (typeof startDate === 'number') {
      startDate = WizDateUtils.numberToDate(startDate);
    }

    if (typeof endDate === 'number') {
      endDate = WizDateUtils.numberToDate(endDate);
    }
    endDate = this.clearTime(endDate);

    let currDate: Date = new Date(startDate.getFullYear(), startDate.getMonth(), startDate.getDate());
    while (this.isBeforeOrSame(currDate, endDate)) {
      result.push(currDate);
      currDate = this.add(currDate, { days: 1 }) as Date;
    }
    return result;
  }

  /**
   * Rince les données horaires d'une date
   * @param pDate La date
   * @return Une nouvelle instance de dates sans les heures
   */
  public static clearTime(pDate: Date | DateTime): Date {
    if (pDate instanceof Date) {
      return new Date(pDate.getFullYear(), pDate.getMonth(), pDate.getDate());
    } else if (pDate instanceof DateTime) {
      return new Date(pDate.year, pDate.month, pDate.day);
    }

    return null;
  }

  // #endregion

  /**
   * Map d'objets  de dates par code locale.
   */
  private static _dateLocaleMap: Map<string, WizDateLocaleObj> = new Map<string, WizDateLocaleObj>();

  /**
   * Recherche de l'objet de configuration par locale
   * @param pLocale La locale
   */
  private static getLocalConfig(pLocale: string): WizDateLocaleObj {
    if (pLocale) {
      if (!this._dateLocaleMap.has(pLocale)) {
        const formatter: Intl.DateTimeFormat = Intl.DateTimeFormat(pLocale);
        const parts: Intl.DateTimeFormatPart[] = formatter.formatToParts(new Date());
        let dateSeparator: string = '/';
        // let datePattern: string = '';
        parts.forEach((element) => {
          // TODO traduire le date pattern ?
          if (element.type === 'literal') {
            // datePattern += element.value;
            dateSeparator = element.value;
          }
        });
        const localeObject: WizDateLocaleObj = new WizDateLocaleObj();
        localeObject.locale = pLocale;
        localeObject.dateSeparator = dateSeparator;
        // localeObject.datePattern = datePattern;
        this._dateLocaleMap.set(pLocale, localeObject);
      }
      return this._dateLocaleMap.get(pLocale);
    }
    return undefined;
  }

  /**
   * Détermination du caractère séparateur d'éléments de dates selon une locale
   */
  public static getLocaleDateSeparator(pLocale: string): string {
    const localeObj: WizDateLocaleObj = this.getLocalConfig(pLocale);
    return localeObj ? localeObj.dateSeparator : '/';
  }

  /**
   * Détermination du caractère séparateur d'éléments d'heures selon une locale
   */
  public static getLocaleHourSeparator(pLocale: string): string {
    const localeObj: WizDateLocaleObj = this.getLocalConfig(pLocale);
    return localeObj ? localeObj.hourSeparator : ':';
  }

  /**
   * Détermination du pattern de dates selon une locale
   */
  public static getLocaleDatePattern(pLocale: string): string {
    const localeObj: WizDateLocaleObj = this.getLocalConfig(pLocale);
    return localeObj ? localeObj.datePattern : '';
  }

  /**
   * Validation d'une heure au format HHmm
   * @param pTimeValue Valeur au format HHmm
   */
  public static isValidTime(pTimeValue: string): boolean {
    let result = false;
    if (pTimeValue && pTimeValue.length == 4) {
      const hourString = pTimeValue.substr(0, 2);
      const minuteString = pTimeValue.substr(2, 2);
      result = this.isValidHour(hourString) && this.isValidMinute(minuteString);
    }
    return result;
  }

  /**
   * Validation d'une heure au format HH
   * @param pHourValue Valeur au format HH
   */
  public static isValidHour(pHourValue: string) {
    const hours: number = WizNumberUtils.parseNumber(pHourValue);
    return WizNumberUtils.isValidNumber(hours) && hours >= 0 && hours <= 23;
  }

  /**
   * Validation d'une minute au format mm
   * @param pHourValue Valeur au format mm
   */
  public static isValidMinute(pMinuteValue: string) {
    const minutes: number = WizNumberUtils.parseNumber(pMinuteValue);
    return WizNumberUtils.isValidNumber(minutes) && minutes >= 0 && minutes <= 59;
  }

  /**
   * Méthode de parse d'heure vers un format HHmm
   * @param value Valeur à parser
   * @return string heure au format HHmm
   */
  public static parseHour(value: string): string {
    if (!value) {
      return undefined;
    }
    const currentLocale: string = this.tradInstance.dateLocale;
    let hourSeparator: string = WizDateUtils.getLocaleHourSeparator(currentLocale);
    if (!hourSeparator) {
      hourSeparator = ':';
    }
    // console.log('WizNativeDateAdapter',currentLocale);

    // collecte du format de la locale:
    let localeMask: string = this.tradInstance.translateKey('hour.format', null, 'HH:mm');

    localeMask = localeMask.replace(' ', hourSeparator); // Pour le meridiem hh:mm a > hh:mm:a

    if (value) {
      for (let i = 0; i < WizDateUtils.ACCEPTED_SEPARATORS.length; i++) {
        const char: string = '\\' + WizDateUtils.ACCEPTED_SEPARATORS[i];
        const regexp: RegExp = new RegExp(char, 'g');
        value = value.replace(regexp, hourSeparator);
      }
    }
    return WizDateUtils.rawStringToHour(value, localeMask);
  }

  /**
   * Retourne le nombre de jours entre 2 dates
   * @param date1 1e date
   * @param date2 2e date
   */
  public static dayDiff(date1: Date, date2: Date) {
/*    const diff:number = Math.abs(date1.getTime() - date2.getTime());
    const diffDays:number = Math.ceil(diff / (1000 * 3600 * 24));
    return diffDays + 1; // LMA patch*/

    // On s'assure de manipuler des dates UTC, on obtient alors une différence entière
    const endDate = WizDateUtils.toUTCDate(date1);
    const startDate = WizDateUtils.toUTCDate(date2);
    const diff:number = Math.abs(endDate.getTime() - startDate.getTime());
    const diffDays:number =  diff / (1000 * 3600 * 24);
    return diffDays + 1;  // On ajoute un jour pour inclure la date de fin dans le compte
  }

  /**
   * Calcule du numéro d'une semaine dans une année
   * @param date la date de début de la semaine
   * @returns une liste contenant le numéro de la semaine et l'année
   */
  public static weekNumber(date: any): number[] {
    date = this.numberToDate(date);
    const target:Date = new Date(date.valueOf());
    const dayNr:number = (date.getDay() + 6) % 7;
    target.setDate(target.getDate() - dayNr + 3);
    const firstThursday:number = target.valueOf();
    target.setMonth(0, 1);
    if (target.getDay() !== 4) {
      target.setMonth(0, 1 + ((4 - target.getDay() + 7) % 7));
    }
    const weekNumber = 1 + Math.ceil((firstThursday - target.valueOf()) / 604800000);
    return [weekNumber, target.getFullYear()];
  }

  /** Timestamp courant */
  public static get currentTimestamp(): number {
    return new Date().getTime();
  }
  
  /**
   * formate une heure HHmmss
   * @param time 
   */
  public static hourToSix(time:string): string {
    let result: string = time || '0';
    while (result.length < 6) {
      result = result + '0';
    }
    return result;
  }

  /**
   * retourne la partie YYYYMMdd d'une date (pas les heures)
   * @param date 
   */
  public static dateToEight(date: string): string {
    let result: string = null;
    if(date?.length >=8) {
      result = date.substring(0,8); 
    }
    return result;
  }

  /**
   * convertit une heure (format texte, brut) en nbr de seconde depuis 00:00:00
   */
  public static timeToSeconds(time: string): number {
    let result: number = null;
    if(isNaN(Number(time))) {
      return result;
    }

    time = this.hourToSix(time);

    let hours = Number(time.slice(0,2));
    let minutes = Number(time.slice(2,4));
    let seconds = Number(time.slice(4,6));
    result = hours*3600 + minutes*60 + seconds;
    return result;
  }

  /**
   * ajoute du temps sur une heure au format HHmmss (ou HHmm ou HH)
   * @param time l'heure
   * @param hour 
   * @param minute 
   * @param second 
   */
  public static timeAdd(time: string, hour: number = 0, minute: number = 0, second: number = 0): string {
    let result: string = time;
    let toSec: number = this.timeToSeconds(time);
    toSec += hour*3600 + minute*60 + second;
    
    // gestion d'un cycle de 24h
    if(toSec >= 0) {
      toSec = toSec%86400; //modulo pour supprimer les jours
    } else { // heure négative. on fait abs modulo et on soustrait de 86400
      toSec = 86400 - (-toSec)%86400;
    }
    result = this.secondsToTime(toSec);

    return result;
  }

  /**
   * retourne la différence en seconde entre 2 heures
   * @param start l'heure de début
   * @param end l'heure de fin
   */
  public static timeDiff(start: string, end: string): number {
    let result: number = 0;
    let toSecStart: number = this.timeToSeconds(start);
    let toSecEnd: number = this.timeToSeconds(end);
    result = toSecEnd - toSecStart;
    
    return result;
  }


  /**
   * convertit un nombre de seconde en heure, format HHmm
   */
  public static secondsToTime(secondTime: number): string {
    let result: string = null;
    if(!secondTime) {
      return null;
    }
    let hours = Math.floor(secondTime / 3600);
    let minutes = Math.floor((secondTime - hours*3600) / 60);
    let seconds = secondTime - hours*3600 - minutes*60;

    result = WizDateUtils.numberOnTwoDigits(hours) + WizDateUtils.numberOnTwoDigits(minutes);

    return result;
  }

  /**
   * formate une date / time pour les objets Calendar YYYY-MM-ddTHH:mm:ss
   */
  public static stringToCalendarTime(pStrDate: string): string {
    let result: string = null;

    if (WizStringUtils.isEmpty(pStrDate)) {
      return null;
    }

    if(pStrDate.length === 8) {
      pStrDate = pStrDate + '000000'; // voila, on a 14
    }

    let yearString = pStrDate.slice(0,4);
    let monthString = pStrDate.slice(4,6);
    let dateString = pStrDate.slice(6,8);
    let hourString = pStrDate.slice(8,10);
    let minuteString = pStrDate.slice(10,12);
    let secondString = pStrDate.slice(12,14);
    
    let datePart = [yearString, monthString, dateString].join('-');
    let hourPart = [hourString, minuteString, secondString].join(':');
    result = [datePart, hourPart].join('T');

    return result;
  }

  /**
   * retourne la partie HH:mm d'un temps sur 14
   */
  public static timeHourPart(pStrDate: string): string {
    let result: string = null;

    if (WizStringUtils.isEmpty(pStrDate)) {
      return null;
    }

    if(pStrDate.length === 8) {
      pStrDate = pStrDate + '000000'; // voila, on a 14
    }
    let hourString = pStrDate.slice(8,10);
    let minuteString = pStrDate.slice(10,12);
    
    result = [hourString, minuteString].join(':');

    return result;
  }

  /**
   * Ajout sur un time
   * @param pDate la date à modifier, string 8 ou 14
   * @param options options de modification
   * @return number | date en fonction du type passé en paramètre
   */
  public static fullTimeAdd(dateStr: string, options: { seconds?: number; minutes?: number; hours?: number; days?: number; months?: number; years?: number }): string {
    let result: string = dateStr;
    
    if(!dateStr) { // pas de date, sortie
      return null;
    }

    while (dateStr.length < 14) { // on passe sur 14
      dateStr = dateStr + '0';
    }

    // ma date
    let date: Date = WizDateUtils.stringToTime(dateStr);
 
    // on passe tout en secondes
    // on ajoute tout
    // et on repasse en date

    if (options?.years) {
      date.setFullYear(date.getFullYear() + options.years);
    }
    if (options?.months) {
      const realDate: number = date.getDate();
      // On passe au jour 1 avant d'ajouter les mois
      date.setDate(1);
      date.setMonth(date.getMonth() + options.months);
      // On remet la date, ou le max du mois si inférieur
      const maxMonthDays: number = this.daysInMonth(date.getMonth(), date.getFullYear());
      date.setDate(maxMonthDays < realDate ? maxMonthDays : realDate);
    }
    if (options?.days) {
      date.setDate(date.getDate() + options.days);
    }

    // nb secondes depuis 1er Janv 1970, avant gestion de l'ajout des heures / min / sec
    let myTime: number = date.getTime() / 1000;

    if (options?.hours) {
      myTime+= options.hours * 3600;
    }
    if (options?.minutes) {
      myTime+= options.minutes * 60;
    }
    if (options?.seconds) {
      myTime+= options.seconds;
    }

    date = new Date(myTime * 1000);
    result = this.dateToString(date, this.FULL_MASK);
    return result;
  }

  /**
   * Retourne l'écart entre 2 dates
   * @param date1 1e date
   * @param date2 2e date
   */
  public static fullTimeDiff(start: string, end: string, format: string = 's'): number {
    let result: number = null;
    if(!start || !end) {
      return null;
    }

    let startDate: Date = this.stringToTime(start);
    let endDate: Date = this.stringToTime(end);

    let diff:number = Math.ceil((endDate.getTime() - startDate.getTime())/1000); // en secondes 
    if(format === 'm') {
      result = Math.ceil(diff/60);
    } else if (format === 'h') {
      result = Math.floor(diff/3600);
      if(result === 0 && startDate.getHours() !== endDate.getHours()) {
        if(diff < 0) {
          result = -1;
        } else {
          result = 1;
        }
      }
    } 
    return result;
  }

  /**
   * Retourne une date convertie en date UTC
   * @para date La date à convertir
   */
  public static toUTCDate(date: Date): Date {
    return new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
  }

}
